/**
 * /speaking-time-calculator and /timecode-calculator (SEO_BRIEF_11 section 2.1): the answer
 * changes as the visitor types, in this tab. The arithmetic is src/tools/speaking-time.ts and
 * src/tools/timecode.ts; this file reads the boxes, writes the answers and says which box can't
 * be read. Every message is in the page's markup (tools/calculator.html), so it is translated with
 * the page; this file only picks which one shows and fills in numbers.
 *
 * WHAT IS COUNTED (lib/usage.ts), and nothing about what was typed:
 *   loaded      `txt`, the first thing typed in a page view (as /text-to-pdf counts its box); not
 *               a file, so the homepage's "files processed" leaves it out (totals.js
 *               `CALCULATOR_TOOLS`)
 *   failed      `txt`, once in a page view, when a box is left holding something it can't read
 *   no_result   sent by lib/usage.ts when a page view typed something and never got an answer:
 *               an answer shown is the result (`resultReached`, as the reading-speed checker's
 *               report read on the page is)
 *   copied      the Copy button (every press), or an answer selected and copied (once); not a
 *               subtitle file, so the homepage's total leaves it out (the Worker's totals.js
 *               `CALCULATOR_TOOLS`)
 * Nothing is saved as a file, so `downloaded` never fires here.
 *
 * A box that can't be read says why after the typing pauses (PROBLEM_PAUSE_MS) or when it is
 * left, never mid-keystroke: "01:" is on its way to a timecode, not a mistake.
 */

import {
  countWords,
  DEFAULT_PACE,
  minutesAndSeconds,
  PACES,
  type PaceName,
  parseDuration,
  parsePace,
  parseWordCount,
  speakingSeconds,
  wordsInTime,
} from '../../../../../src/tools/speaking-time.ts';
import {
  calculate,
  convert,
  type Converted,
  DEFAULT_RATE,
  formatTimecode,
  nominalFps,
  type Operation,
  type Problem,
  rateById,
  type TimecodeRate,
  type Unit,
  UNITS,
} from '../../../../../src/tools/timecode.ts';
import { copyText } from '../lib/clipboard.ts';
import { countCopy, watchCopies } from '../lib/copies.ts';
import { data, on, qs, qsa } from '../lib/dom.ts';
import { toast } from '../lib/toast.ts';
import { countUse, resultReached } from '../lib/usage.ts';

/** How long typing pauses before a box that can't be read says why. */
const PROBLEM_PAUSE_MS = 700;

/* ── counting ─────────────────────────────────────────────────────────── */

export interface CalcUsage {
  /** Something was typed. Counted once per page view. */
  typed(): void;
  /** A box was left holding something it can't read. Counted once per page view. */
  unreadable(): void;
  /** An answer is on the screen: leaving now is not a no-result. */
  answered(): void;
}

/** The page's three counts, each sent at most once in a page view. */
export function calcUsage(
  tool: string,
  count: (event: 'loaded' | 'failed', tool: string, format: 'txt') => void = countUse,
  reached: (tool: string) => void = resultReached,
): CalcUsage {
  let typed = false;
  let failed = false;
  let answered = false;
  return {
    typed() {
      if (typed) return;
      typed = true;
      count('loaded', tool, 'txt');
    },
    unreadable() {
      if (failed) return;
      failed = true;
      count('failed', tool, 'txt');
    },
    answered() {
      if (answered) return;
      answered = true;
      reached(tool);
    },
  };
}

/* ── the speaking-time calculator ─────────────────────────────────────── */

export type SpeakingMode = 'time' | 'words';
export type SpeakingProblem = 'count' | 'pace' | 'time';

/** What the boxes hold, as typed. `pace` is the chosen pace's value: a PACES name or 'custom'. */
export interface SpeakingInputs {
  readonly mode: SpeakingMode;
  readonly count: string;
  readonly pace: string;
  readonly custom: string;
  readonly minutes: string;
  readonly seconds: string;
}

export interface SpeakingAnswer {
  /** The boxes that hold something they can't read, for the open mode. */
  readonly problems: readonly SpeakingProblem[];
  /** Words to time: the answer, or null while there is none. */
  readonly time: { readonly minutes: number; readonly seconds: number } | null;
  /** Time to words: the answer, or null while there is none. */
  readonly words: number | null;
}

/** The pace in words a minute, null while a custom pace is empty or out of range. */
function paceOf(i: SpeakingInputs): number | null {
  if (i.pace === 'custom') return i.custom.trim() === '' ? null : parsePace(i.custom);
  return PACES[i.pace as PaceName] ?? PACES[DEFAULT_PACE];
}

/** The answer for what the boxes hold. Empty boxes are no answer and no problem. */
export function speakingAnswer(i: SpeakingInputs): SpeakingAnswer {
  const problems: SpeakingProblem[] = [];
  const pace = paceOf(i);
  if (i.pace === 'custom' && i.custom.trim() !== '' && pace === null) problems.push('pace');
  if (i.mode === 'time') {
    const empty = i.count.trim() === '';
    const words = empty ? null : parseWordCount(i.count);
    if (!empty && words === null) problems.push('count');
    const time =
      words !== null && words > 0 && pace !== null
        ? minutesAndSeconds(speakingSeconds(words, pace))
        : null;
    return { problems, time, words: null };
  }
  const empty = i.minutes.trim() === '' && i.seconds.trim() === '';
  const seconds = empty ? null : parseDuration(i.minutes, i.seconds);
  if (!empty && seconds === null) problems.push('time');
  const words =
    seconds !== null && seconds > 0 && pace !== null ? wordsInTime(seconds, pace) : null;
  return { problems, time: null, words };
}

/** The word count box's value for a pasted or typed text: its words, or empty with none. */
export function countFor(text: string): string {
  const n = countWords(text);
  return n > 0 ? String(n) : '';
}

function mountSpeaking(root: HTMLElement, use: CalcUsage, tool: string): void {
  const text = qs<HTMLTextAreaElement>('[data-calc-text]', root);
  const count = qs<HTMLInputElement>('[data-calc-count]', root);
  const minutes = qs<HTMLInputElement>('[data-calc-minutes]', root);
  const seconds = qs<HTMLInputElement>('[data-calc-seconds]', root);
  const custom = qs<HTMLInputElement>('[data-calc-custom-pace]', root);
  const customField = qs<HTMLElement>('[data-calc-custom]', root);
  const modeRadios = qsa<HTMLInputElement>('[data-calc-mode]', root);
  const paceRadios = qsa<HTMLInputElement>('[data-calc-pace]', root);
  const copy = qs<HTMLButtonElement>('[data-calc-copy]', root);
  const fields: Partial<Record<SpeakingProblem, HTMLInputElement | null>> = {
    count,
    pace: custom,
    time: minutes,
  };
  const problems = new Problems(root);

  const inputs = (): SpeakingInputs => ({
    mode: modeRadios.find((r) => r.checked)?.value === 'words' ? 'words' : 'time',
    count: count?.value ?? '',
    pace: paceRadios.find((r) => r.checked)?.value ?? DEFAULT_PACE,
    custom: custom?.value ?? '',
    minutes: minutes?.value ?? '',
    seconds: seconds?.value ?? '',
  });

  function paint(now: boolean): void {
    const i = inputs();
    for (const pane of qsa<HTMLElement>('[data-calc-pane]', root)) {
      pane.hidden = data(pane, 'calc-pane') !== i.mode;
    }
    for (const box of qsa<HTMLElement>('[data-calc-answer]', root)) {
      box.hidden = data(box, 'calc-answer') !== i.mode;
    }
    if (customField) customField.hidden = i.pace !== 'custom';
    const answer = speakingAnswer(i);
    for (const key of ['count', 'pace', 'time'] as const) {
      problems.set(key, answer.problems.includes(key) ? key : null, fields[key] ?? null, now);
    }
    const shown = i.mode === 'time' ? answer.time : answer.words;
    if (answer.time) {
      setSlot(root, 'minutes', answer.time.minutes.toLocaleString(pageLang()));
      setSlot(root, 'seconds', String(answer.time.seconds));
    } else if (i.mode === 'time') {
      setSlot(root, 'minutes', '0');
      setSlot(root, 'seconds', '0');
    }
    if (i.mode === 'words') setSlot(root, 'words', (answer.words ?? 0).toLocaleString(pageLang()));
    if (copy) copy.disabled = shown === null;
    if (shown !== null) use.answered();
  }

  const typed = (box: HTMLInputElement | HTMLTextAreaElement | null): void => {
    if (box && box.value.trim() !== '') use.typed();
  };

  if (text) {
    on(text, 'input', () => {
      typed(text);
      if (count) count.value = countFor(text.value);
      paint(false);
    });
  }
  for (const box of [count, custom, minutes, seconds]) {
    if (!box) continue;
    on(box, 'input', () => {
      typed(box);
      paint(false);
    });
    on(box, 'change', () => {
      paint(true);
      if (speakingAnswer(inputs()).problems.length > 0) use.unreadable();
    });
  }
  for (const radio of modeRadios) on(radio, 'change', () => paint(true));
  for (const radio of paceRadios) {
    on(radio, 'change', () => {
      paint(true);
      if (radio.checked && radio.value === 'custom') custom?.focus();
    });
  }
  wireCopy(copy, tool, () => {
    const open = qsa<HTMLElement>('[data-calc-answer]', root).find((box) => !box.hidden);
    const result = open ? qs<HTMLElement>('[data-calc-result]', open) : null;
    return result?.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  });
  watchCopies(qsa('[data-calc-result]', root), tool);

  // Text the browser kept in the boxes across a reload or a return.
  if (text && text.value.trim() !== '' && count && count.value.trim() === '') {
    count.value = countFor(text.value);
  }
  paint(false);
}

/* ── the timecode calculator ──────────────────────────────────────────── */

export interface SumView {
  /** Why each box can't be read, or null when it can (or is empty). */
  readonly a: Problem | null;
  readonly b: Problem | null;
  /** The answer, or null while there is none. */
  readonly result: { readonly frames: number; readonly timecode: string } | null;
}

const readProblem = (r: { ok: boolean; problem?: Problem }): Problem | null =>
  r.ok || r.problem === 'empty' ? null : (r.problem ?? null);

/**
 * `a` plus or minus `b`. An empty box counts as zero while the other holds a timecode, so the
 * first box alone shows itself; both empty is no answer.
 */
export function sumView(a: string, op: Operation, b: string, rate: TimecodeRate): SumView {
  if (a.trim() === '' && b.trim() === '') return { a: null, b: null, result: null };
  const sum = calculate(a.trim() === '' ? '0' : a, op, b.trim() === '' ? '0' : b, rate);
  return { a: readProblem(sum.a), b: readProblem(sum.b), result: sum.result };
}

export interface ConvertView {
  readonly problem: Problem | null;
  readonly values: Converted | null;
}

/** One converter box read and written as all four; an empty box is no answer and no problem. */
export function convertView(unit: Unit, input: string, rate: TimecodeRate): ConvertView {
  const read = convert(unit, input, rate);
  if (read.ok) return { problem: null, values: read.value };
  return { problem: readProblem(read), values: null };
}

/** The value a converter box shows for a converted moment. */
export function unitValue(values: Converted, unit: Unit): string {
  return unit === 'frames' ? String(values.frames) : values[unit];
}

function mountTimecode(root: HTMLElement, use: CalcUsage, tool: string): void {
  const rateSelect = qs<HTMLSelectElement>('[data-calc-rate]', root);
  const a = qs<HTMLInputElement>('[data-calc-tc="a"]', root);
  const b = qs<HTMLInputElement>('[data-calc-tc="b"]', root);
  const opRadios = qsa<HTMLInputElement>('[data-calc-op]', root);
  const copy = qs<HTMLButtonElement>('[data-calc-copy]', root);
  const units = new Map<Unit, HTMLInputElement>();
  for (const unit of UNITS) {
    const box = qs<HTMLInputElement>(`[data-calc-unit="${unit}"]`, root);
    if (box) units.set(unit, box);
  }
  const problems = new Problems(root);
  /** The converter box the visitor last typed in; the others follow it. */
  let source: Unit = 'timecode';

  const rate = (): TimecodeRate =>
    rateById(rateSelect?.value ?? DEFAULT_RATE) ?? (rateById(DEFAULT_RATE) as TimecodeRate);
  const op = (): Operation =>
    opRadios.find((r) => r.checked)?.value === 'subtract' ? 'subtract' : 'add';

  function paintSum(now: boolean): void {
    const view = sumView(a?.value ?? '', op(), b?.value ?? '', rate());
    problems.set('calc-a', view.a, a, now, rate());
    problems.set('calc-b', view.b, b, now, rate());
    // No answer reads as zero at the rate chosen: 00:00:00;00 in drop-frame.
    setSlot(root, 'timecode', view.result?.timecode ?? formatTimecode(0, rate()));
    setSlot(root, 'frames', (view.result?.frames ?? 0).toLocaleString(pageLang()));
    if (copy) copy.disabled = view.result === null;
    if (view.result) use.answered();
  }

  function paintConvert(now: boolean): void {
    const box = units.get(source);
    const view = convertView(source, box?.value ?? '', rate());
    for (const [unit, other] of units) {
      const id = `calc-unit-${unit}`;
      if (unit === source) {
        problems.set(id, view.problem, other, now, rate());
        continue;
      }
      problems.set(id, null, other, true);
      other.value = view.values ? unitValue(view.values, unit) : '';
    }
    if (view.values) use.answered();
  }

  const sumUnreadable = (): boolean => {
    const view = sumView(a?.value ?? '', op(), b?.value ?? '', rate());
    return view.a !== null || view.b !== null;
  };

  for (const box of [a, b]) {
    if (!box) continue;
    on(box, 'input', () => {
      if (box.value.trim() !== '') use.typed();
      paintSum(false);
    });
    on(box, 'change', () => {
      paintSum(true);
      if (sumUnreadable()) use.unreadable();
    });
  }
  for (const radio of opRadios) on(radio, 'change', () => paintSum(true));
  for (const [unit, box] of units) {
    on(box, 'input', () => {
      source = unit;
      if (box.value.trim() !== '') use.typed();
      paintConvert(false);
    });
    on(box, 'change', () => {
      source = unit;
      paintConvert(true);
      if (convertView(unit, box.value, rate()).problem !== null) use.unreadable();
    });
  }
  if (rateSelect) {
    on(rateSelect, 'change', () => {
      paintSum(true);
      paintConvert(true);
    });
  }
  wireCopy(copy, tool, () => qs('[data-slot="timecode"]', root)?.textContent?.trim() ?? '');
  watchCopies([...qsa('[data-calc-result]', root), qs('.calc__grid', root)], tool);

  // Boxes the browser kept across a reload or a return: the first filled converter box leads.
  source = UNITS.find((u) => (units.get(u)?.value ?? '').trim() !== '') ?? 'timecode';
  paintSum(false);
  paintConvert(false);
}

/* ── shared ───────────────────────────────────────────────────────────── */

/**
 * The messages under the boxes. A problem is shown at once when `now` (the box was left, or a
 * choice changed) and otherwise after the typing pauses; a box that reads again hides its message
 * at once. The box is marked `aria-invalid` while its message shows.
 *
 * Two kinds of message: one per problem on the speaking page (`data-calc-error="count"`), and one
 * block per box with a line per reason on the timecode page (`data-calc-error-for="calc-a"`,
 * `data-calc-problem="drop"`).
 */
class Problems {
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly root: HTMLElement;
  constructor(root: HTMLElement) {
    this.root = root;
  }

  set(
    key: string,
    problem: string | null,
    box: HTMLInputElement | null,
    now: boolean,
    rate?: TimecodeRate,
  ): void {
    const pending = this.timers.get(key);
    if (pending !== undefined) clearTimeout(pending);
    this.timers.delete(key);
    if (problem === null || now) {
      this.show(key, problem, box, rate);
      return;
    }
    this.timers.set(
      key,
      setTimeout(() => {
        this.timers.delete(key);
        this.show(key, problem, box, rate);
      }, PROBLEM_PAUSE_MS),
    );
  }

  private show(
    key: string,
    problem: string | null,
    box: HTMLInputElement | null,
    rate?: TimecodeRate,
  ): void {
    const single = qs<HTMLElement>(`[data-calc-error="${key}"]`, this.root);
    if (single) single.hidden = problem === null;
    const block = qs<HTMLElement>(`[data-calc-error-for="${key}"]`, this.root);
    if (block) {
      let any = false;
      for (const line of qsa<HTMLElement>('[data-calc-problem]', block)) {
        line.hidden = data(line, 'calc-problem') !== problem;
        any ||= !line.hidden;
      }
      block.hidden = !any;
      const max = qs<HTMLElement>('[data-slot="max"]', block);
      if (max && rate) max.textContent = String(nominalFps(rate) - 1).padStart(2, '0');
    }
    if (box) {
      if (problem === null) box.removeAttribute('aria-invalid');
      else box.setAttribute('aria-invalid', 'true');
    }
  }
}

function setSlot(root: HTMLElement, name: string, value: string): void {
  for (const el of qsa<HTMLElement>(`[data-slot="${name}"]`, root)) el.textContent = value;
}

function pageLang(): string | undefined {
  return typeof document === 'undefined' ? undefined : document.documentElement.lang || undefined;
}

/** Copy puts the answer on the clipboard, says so, and is counted on every press that landed. */
function wireCopy(button: HTMLButtonElement | null, tool: string, text: () => string): void {
  if (!button) return;
  on(button, 'click', () => {
    const answer = text();
    if (answer === '') return;
    void copyText(answer).then((ok) => {
      if (!ok) return;
      toast(data(button, 'copied-message') ?? '');
      countCopy(tool);
    });
  });
}

export function mountCalculatorPage(): void {
  const root = qs<HTMLElement>('[data-calc]');
  if (!root) return;
  const kind = data(root, 'calc');
  const tool =
    data(root, 'usage-tool') ??
    (kind === 'speaking' ? 'speaking-time-calculator' : 'timecode-calculator');
  const use = calcUsage(tool);
  if (kind === 'speaking') mountSpeaking(root, use, tool);
  else mountTimecode(root, use, tool);
}
